import { useEffect, useState } from "react";
import { PhoneIncoming, PhoneOutgoing, Mic, AlertTriangle, ChevronRight } from "lucide-react";
import { Dot, Skel, fmtDur, outcomeOf, whenParts } from "@/components/shally/ui";

const Dir = ({ d }) => (d === "outbound"
  ? <PhoneOutgoing className="h-3.5 w-3.5 text-muted-foreground" aria-label="Outgoing" />
  : <PhoneIncoming className="h-3.5 w-3.5 text-muted-foreground" aria-label="Incoming" />);

function Flags({ c }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      {c.emergency && <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400"><AlertTriangle className="h-3 w-3" />Urgent</span>}
      {c.follow_up && <Dot tone="amber" label="Follow-up" />}
    </span>);
}

const who = (c) => c.customer || c.caller || "Unknown caller";

function Row({ c, onOpen, action }) {
  const o = outcomeOf(c), [d, t] = whenParts(c.started_at);
  return (
    <tr className="group cursor-pointer border-t border-border/70 transition-colors hover:bg-muted/40" onClick={() => onOpen(c.id)} data-testid={`ci-row-${c.id}`}>
      <td className="py-3 pl-5 pr-3"><div className="flex items-center gap-2.5"><Dir d={c.direction} />
        <div className="min-w-0"><p className="truncate font-semibold text-foreground">{who(c)}</p>{c.customer && <p className="text-xs text-muted-foreground">{c.caller}</p>}</div></div></td>
      <td className="px-3 py-3 text-foreground/80"><p>{d}</p><p className="text-xs text-muted-foreground">{t}</p></td>
      <td className="px-3 py-3 tabular-nums text-foreground/80">{fmtDur(c.duration_s)}</td>
      <td className="max-w-xs px-3 py-3"><p className="line-clamp-2 text-foreground/80">{c.headline || <span className="text-muted-foreground">—</span>}</p></td>
      <td className="px-3 py-3"><Dot tone={o.tone} label={o.label} /></td>
      <td className="px-3 py-3">{c.recording === "available" ? <Mic className="h-4 w-4 text-primary" aria-label="Recording available" /> : <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-3"><Flags c={c} /></td>
      <td className="py-3 pl-3 pr-5 text-right" onClick={(e) => e.stopPropagation()}>
        <span className="inline-flex items-center gap-1">{action?.(c)}
          <button type="button" onClick={() => onOpen(c.id)} data-testid={`ci-open-${c.id}`} aria-label="View call"
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><ChevronRight className="h-4 w-4" /></button></span></td>
    </tr>);
}

function Card({ c, onOpen, action }) {
  const o = outcomeOf(c), [d, t] = whenParts(c.started_at);
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(c.id)} onKeyDown={(e) => { if (e.key === "Enter") { onOpen(c.id); } }} data-testid={`ci-card-${c.id}`}
      className="cursor-pointer border-t border-border/70 px-4 py-3.5 transition-colors first:border-t-0 active:bg-muted/50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2"><Dir d={c.direction} /><p className="truncate text-sm font-semibold text-foreground">{who(c)}</p></div>
        <span className="shrink-0 text-xs text-muted-foreground">{d} · {t}</span>
      </div>
      {c.headline && <p className="mt-1 line-clamp-2 text-sm text-foreground/75">{c.headline}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
        <Dot tone={o.tone} label={o.label} /><span className="tabular-nums">{fmtDur(c.duration_s)}</span>
        {c.recording === "available" && <span className="inline-flex items-center gap-1"><Mic className="h-3 w-3 text-primary" />Recording</span>}
        <Flags c={c} />
        {action && <span className="ml-auto" onClick={(e) => e.stopPropagation()}>{action(c)}</span>}
      </div>
    </div>);
}

const MQ = "(min-width: 1024px)";
function useWide() {
  const [w, setW] = useState(() => window.matchMedia(MQ).matches);
  useEffect(() => {
    const m = window.matchMedia(MQ), f = () => setW(m.matches);
    m.addEventListener("change", f);
    return () => { m.removeEventListener("change", f); };
  }, []);
  return w;
}

export function CallList({ items, loading, onOpen, empty, action, testid = "ci-table" }) {
  const wide = useWide();
  if (loading) return <div className="space-y-3 p-5" data-testid={`${testid}-loading`}>{[0, 1, 2, 3].map((i) => <Skel key={i} className="h-10 w-full" />)}</div>;
  if (!items?.length) return empty;
  if (wide) return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {["Caller", "When", "Duration", "Summary", "Outcome", "Rec.", "Attention", ""].map((h, i) => <th key={h || i} className={`py-2.5 font-semibold ${i === 0 ? "pl-5 pr-3" : "px-3"}`}>{h}</th>)}
          </tr></thead>
          <tbody>{items.map((c) => <Row key={c.id} c={c} onOpen={onOpen} action={action} />)}</tbody>
        </table>
      </div>);
  return <div data-testid={`${testid}-cards`}>{items.map((c) => <Card key={c.id} c={c} onOpen={onOpen} action={action} />)}</div>;
}
